import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import type { OutreachPlannedPitch } from '../../outreachTypes';
import { PitchKindCover } from '../PitchKindCover';
import { isPitchKind, pitchKindName } from '../pitchKindCopy';
import { peopleMessages } from '../peopleMessages';
import { PlannedStateCell } from './PlannedStateCell';
import { whenLines } from './upNextCopy';
import { UntilText } from './UntilText';
import { upNextMessages as messages } from './upNextMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Up next; each part uses its own slice of it */
import styles from './UpNext.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface UpNextRowProps {
  pitch: OutreachPlannedPitch;
  now: Date;
  /** The row came after the first view of the card: it enters once (design s1). */
  isNew: boolean;
}

/** One planned pitch (design s2-s4): cover, name, kind and followers, the subject line, when, state. */
const UpNextRowComponent: React.FC<UpNextRowProps> = ({ pitch, now, isNew }) => {
  const intl = useIntl();
  const kind = isPitchKind(pitch.kind) ? pitch.kind : null;
  const sub = useMemo(() => {
    const parts = [kind === null ? intl.formatMessage(peopleMessages.ownContact) : pitchKindName(intl, kind)];
    if (pitch.followers !== null) {
      parts.push(intl.formatMessage(peopleMessages.followers, { count: pitch.followers }));
    }
    return parts.join(' · ');
  }, [intl, kind, pitch.followers]);
  const when = whenLines(intl, pitch, now);

  const preview = () => {
    if (pitch.state === 'writing') {
      return <span className={styles.skeletonLine} aria-hidden />;
    }
    if (pitch.subject === null) {
      return (
        <span className={clsx(styles.preview, styles.previewMuted)}>{intl.formatMessage(messages.waitsForPlug)}</span>
      );
    }
    return <span className={styles.preview}>{`“${pitch.subject}”`}</span>;
  };

  return (
    <div
      className={clsx(
        styles.row,
        (pitch.state === 'sending' || pitch.state === 'writing') && styles.rowNow,
        isNew && styles.rowIn
      )}
      data-testid="up-next-row"
    >
      <PitchKindCover coverUrl={pitch.coverUrl} kind={kind} />
      <div className={styles.who}>
        <b>{pitch.name}</b>
        <small>{sub}</small>
        {pitch.sendAt === null && preview()}
      </div>
      <div className={clsx(styles.when, styles.rowWhen)}>
        <b>{when.main}</b>
        {when.sub !== '' && <small>{when.sub}</small>}
      </div>
      <PlannedStateCell state={pitch.state} until={pitch.sendAt === null ? null : <UntilText iso={pitch.sendAt} />} />
    </div>
  );
};

export const UpNextRow = TypedMemo(UpNextRowComponent);
